import { Link } from "react-router-dom";
import Reveal from "../components/site/Reveal";
import Seo from "../components/site/Seo";
import { BRAND } from "../lib/site-data";

// Shared layout for legal/policy documents
function PolicyShell({ title, intro, sections, testid, seoTitle, seoDescription, seoPath }) {
  return (
    <main data-testid={testid} className="overflow-hidden">
      <Seo title={seoTitle} description={seoDescription} path={seoPath} />
      <section className="pt-40 pb-16 bg-[#faf6f0]">
        <div className="container-x">
          <p className="font-label text-[#f59e0b] mb-6">Legal · Ocean Oasis Group</p>
          <h1
            className="font-display text-[#0a3d7a] leading-[0.94] max-w-5xl"
            style={{ fontSize: "clamp(2.4rem, 7vw, 6rem)" }}
          >
            {title}
          </h1>
          <p className="font-body text-base text-[#0a3d7a]/70 mt-8 max-w-2xl">
            Last updated: {new Date().toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "long",
              year: "numeric",
            })}
          </p>
        </div>
      </section>

      <section className="pb-24 md:pb-32 bg-[#faf6f0]">
        <div className="container-x">
          <div className="grid lg:grid-cols-12 gap-12">
            <aside className="lg:col-span-3">
              <Reveal>
                <div className="lg:sticky lg:top-32">
                  <p className="font-label text-[#f59e0b] mb-4">Contents</p>
                  <ul className="space-y-2 font-body text-sm">
                    {sections.map((s, i) => (
                      <li key={s.id}>
                        <a
                          href={`#${s.id}`}
                          className="text-[#0a3d7a]/80 hover:text-[#f59e0b] transition-colors"
                        >
                          {String(i + 1).padStart(2, "0")} · {s.h}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            </aside>

            <article className="lg:col-span-8 lg:col-start-5 space-y-12">
              {intro && (
                <Reveal>
                  <p className="font-display italic text-2xl md:text-3xl text-[#0a3d7a] leading-[1.3]">
                    {intro}
                  </p>
                </Reveal>
              )}
              {sections.map((s, i) => (
                <Reveal key={s.id} delay={Math.min(i, 6) * 0.05}>
                  <section id={s.id} className="scroll-mt-32">
                    <p className="font-label text-[#f59e0b] mb-3">
                      Section {String(i + 1).padStart(2, "0")}
                    </p>
                    <h2 className="font-display text-3xl md:text-4xl text-[#0a3d7a] mb-5 leading-tight">
                      {s.h}
                    </h2>
                    <div className="font-body text-base text-[#0a3d7a]/80 leading-relaxed space-y-4">
                      {s.body}
                    </div>
                  </section>
                </Reveal>
              ))}

              <Reveal>
                <div className="mt-16 pt-10 border-t border-[rgba(10,61,122,0.15)]">
                  <p className="font-label text-[#f59e0b] mb-3">Questions?</p>
                  <p className="font-body text-base text-[#0a3d7a]/80 leading-relaxed">
                    Write to us at{" "}
                    <a
                      href={`mailto:${BRAND.contact.email}`}
                      className="underline decoration-[#f59e0b] underline-offset-2 hover:text-[#f59e0b]"
                    >
                      {BRAND.contact.email}
                    </a>{" "}
                    or visit us at {BRAND.address.line1},{" "}
                    {BRAND.address.line2}, {BRAND.address.city},{" "}
                    {BRAND.address.country}.
                  </p>
                  <div className="mt-6">
                    <Link
                      to="/"
                      data-testid="policy-back-home"
                      className="btn-outline"
                    >
                      Back to Home
                    </Link>
                  </div>
                </div>
              </Reveal>
            </article>
          </div>
        </div>
      </section>
    </main>
  );
}

export function CookiesPolicy() {
  const sections = [
    {
      id: "what",
      h: "What are cookies?",
      body: (
        <>
          <p>
            Cookies are small text files that websites place on your device to
            remember preferences, understand usage and deliver a better
            experience. They cannot read information stored elsewhere on your
            computer.
          </p>
        </>
      ),
    },
    {
      id: "how",
      h: "How Ocean Oasis uses cookies",
      body: (
        <>
          <p>We use cookies for three purposes:</p>
          <ul className="list-none pl-0 space-y-3">
            <li className="pl-6 relative">
              <span className="absolute left-0 top-2 w-2 h-2 bg-[#f59e0b]" />
              <strong className="font-semibold text-[#0a3d7a]">
                Strictly necessary
              </strong>{" "}
              — required to operate the site, remember your cookie preference
              and provide secure browsing.
            </li>
            <li className="pl-6 relative">
              <span className="absolute left-0 top-2 w-2 h-2 bg-[#f59e0b]" />
              <strong className="font-semibold text-[#0a3d7a]">
                Analytics
              </strong>{" "}
              — to understand which pages visitors find useful and improve our
              content. These cookies do not personally identify you.
            </li>
            <li className="pl-6 relative">
              <span className="absolute left-0 top-2 w-2 h-2 bg-[#f59e0b]" />
              <strong className="font-semibold text-[#0a3d7a]">
                Marketing
              </strong>{" "}
              — to occasionally deliver relevant Ocean Oasis communications.
              These are optional and disabled by default.
            </li>
          </ul>
        </>
      ),
    },
    {
      id: "manage",
      h: "Managing your preferences",
      body: (
        <>
          <p>
            When you first visit the website, you'll see a cookie banner with
            three options: <em className="italic">Accept All</em>,{" "}
            <em className="italic">Reject Non-Essential</em>, or{" "}
            <em className="italic">Customise</em>. You can change your choice
            at any time by clearing your browser storage for this site, which
            will re-display the banner on your next visit.
          </p>
          <p>
            Most browsers also allow you to control cookies through their
            settings — including blocking all cookies, deleting existing
            cookies, or notifying you when one is set.
          </p>
        </>
      ),
    },
    {
      id: "third",
      h: "Third-party services",
      body: (
        <>
          <p>
            We may use trusted third-party providers (such as analytics and map
            embedding services) which set their own cookies. We do not control
            these cookies; please refer to those providers' privacy notices for
            full details.
          </p>
        </>
      ),
    },
    {
      id: "changes",
      h: "Changes to this policy",
      body: (
        <>
          <p>
            We may update this Cookies Policy from time to time. The "Last
            updated" date at the top of this page reflects the most recent
            revision. Continued use of the site after changes constitutes
            acceptance of the updated policy.
          </p>
        </>
      ),
    },
  ];
  return (
    <PolicyShell
      testid="page-cookies-policy"
      title="Cookies Policy"
      seoTitle="Cookies Policy | Ocean Oasis"
      seoDescription="How Ocean Oasis uses cookies on oceanoasis.llc — strictly necessary, analytics and marketing cookies, and how to manage your preferences."
      seoPath="/cookies-policy"
      intro="We use a small number of cookies to keep this site working, understand how visitors use it, and — only with your permission — to share occasional Ocean Oasis communications."
      sections={sections}
    />
  );
}

export function PrivacyPolicy() {
  const sections = [
    {
      id: "intro",
      h: "Introduction",
      body: (
        <p>
          Ocean Oasis Group ("we", "our", "us") operates three divisions —
          Ocean Oasis Supermarket, Ocean Oasis Logistics and Ocean Oasis Coffee
          — from Sundus Plaza, Bondhere District, Mogadishu, Somalia. This
          notice explains what information we collect through this website and
          how we look after it.
        </p>
      ),
    },
    {
      id: "data",
      h: "What we collect",
      body: (
        <p>
          This website is informational. We do not require you to create an
          account or submit personal information. We may automatically collect
          standard log data (browser type, device, approximate location and
          pages visited) and, with your consent, analytics data via cookies.
        </p>
      ),
    },
    {
      id: "use",
      h: "How we use information",
      body: (
        <p>
          We use the limited data we collect to operate the site, understand
          which content is helpful, and improve our communications. We do not
          sell personal information.
        </p>
      ),
    },
    {
      id: "share",
      h: "Sharing",
      body: (
        <p>
          We share information only with trusted service providers required to
          operate the website (such as hosting and analytics) and where we are
          required to do so by law.
        </p>
      ),
    },
    {
      id: "rights",
      h: "Your rights",
      body: (
        <p>
          You may contact us at any time to ask what information we hold about
          you, to correct it, or to ask us to delete it. Reach us at{" "}
          {BRAND.contact.email}.
        </p>
      ),
    },
    {
      id: "security",
      h: "Security",
      body: (
        <p>
          We use reasonable technical and organisational measures to protect
          information. No method of transmission over the internet is fully
          secure — please share sensitive information cautiously.
        </p>
      ),
    },
  ];
  return (
    <PolicyShell
      testid="page-privacy-policy"
      title="Privacy Policy"
      seoTitle="Privacy Policy | Ocean Oasis"
      seoDescription="How Ocean Oasis Group handles the limited information collected through oceanoasis.llc — what we collect, how we use it, and your rights."
      seoPath="/privacy-policy"
      intro="A clear, plain-language summary of how Ocean Oasis handles the limited information we collect through this website."
      sections={sections}
    />
  );
}

export function Terms() {
  const sections = [
    {
      id: "use",
      h: "Use of the website",
      body: (
        <p>
          You may use this website for lawful, personal and informational
          purposes only. By accessing the site, you agree not to attempt to
          interfere with its operation, security or content.
        </p>
      ),
    },
    {
      id: "ip",
      h: "Intellectual property",
      body: (
        <p>
          All trademarks, logos, design elements and written content on this
          site are the property of Ocean Oasis Group or its respective
          divisions. You may not copy, reproduce or republish content without
          our prior written permission.
        </p>
      ),
    },
    {
      id: "disclaimers",
      h: "Disclaimers",
      body: (
        <p>
          Information on this site is provided "as is" for general purposes.
          While we take care to keep it accurate, we make no guarantees of
          completeness, suitability or availability. Prices, services and
          offerings may change without notice.
        </p>
      ),
    },
    {
      id: "links",
      h: "External links",
      body: (
        <p>
          Our site may link to third-party websites for convenience. We do not
          control or endorse those sites and are not responsible for their
          content.
        </p>
      ),
    },
    {
      id: "law",
      h: "Governing law",
      body: (
        <p>
          These Terms are governed by the laws of the Federal Republic of
          Somalia. Any disputes arising in connection with the website will be
          subject to the exclusive jurisdiction of the courts in Mogadishu.
        </p>
      ),
    },
  ];
  return (
    <PolicyShell
      testid="page-terms"
      title="Terms of Use"
      seoTitle="Terms of Use | Ocean Oasis"
      seoDescription="The terms governing your use of the Ocean Oasis website — intellectual property, disclaimers, external links and governing law."
      seoPath="/terms"
      intro="The terms below govern your use of this website. By browsing, you agree to them."
      sections={sections}
    />
  );
}
